import React, { useState } from "react";
import Icon from "./Icon";
import { productCategoryOptions } from "../data/siteData";
import "../styles/QuoteForm.css";

const initialState = {
  fullName: "",
  companyName: "",
  email: "",
  country: "",
  phone: "",
  productCategory: productCategoryOptions[0],
  products: "",
  quantity: "",
  requirements: "",
};

/*
 * Self-contained quote request form.
 * Manages its own state and a mock inline "submit" — no backend wired
 * up, but structured so a fetch() call can be dropped into handleSubmit.
 */
export default function QuoteForm() {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.fullName || !form.email || !form.phone) {
      setError("Please fill in your name, email, and phone number.");
      return;
    }

    setError("");

    const whatsappNumber = "919514532527"; // Replace with your WhatsApp number

    const message = `
*New Quote Request - Terra Bloom Studio*

*Customer Details*
Name: ${form.fullName}
Company: ${form.companyName || "Not provided"}
Email: ${form.email}
Phone / WhatsApp: ${form.phone}
Country: ${form.country || "Not provided"}

*Order Details*
Product Category: ${form.productCategory}
Products / SKUs: ${form.products || "Not provided"}
Order Quantity: ${form.quantity || "Not provided"}

*Additional Requirements*
${form.requirements || "Not provided"}
  `.trim();

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank");

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="tp-quote-form tp-quote-form--success">
        <Icon name="checkCircle" size={44} />
        <h3>Thank you, {form.fullName.split(" ")[0]}!</h3>
        <p>
          Your quote details have been prepared in WhatsApp. Please send the
          message to complete your request. We respond within 24–48
          business hours. Our team will reach out to you shortly.
        </p>
        <button
          className="tp-btn tp-btn--outline"
          onClick={() => setForm(initialState) || setSubmitted(false)}
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form className="tp-quote-form" onSubmit={handleSubmit}>
      <h3 className="tp-quote-form__title">Request a Personalized Quote</h3>
      <p className="tp-quote-form__sub">
        For buyers, brands, and retailers looking to discuss pricing, samples,
        and bulk orders.
      </p>

      <div className="tp-quote-form__grid">
        <label className="tp-quote-form__field">
          <span>Full Name</span>
          <input
            type="text"
            name="fullName"
            placeholder="Enter Full Name"
            value={form.fullName}
            onChange={handleChange}
            required
          />
        </label>

        <div className="tp-quote-form__field tp-quote-form__field--wide">
          <span>Product Category</span>
          <div className="tp-quote-form__toggle">
            {productCategoryOptions.map((option) => (
              <button
                type="button"
                key={option}
                className={`tp-quote-form__toggle-btn ${
                  form.productCategory === option
                    ? "tp-quote-form__toggle-btn--active"
                    : ""
                }`}
                onClick={() =>
                  setForm((prev) => ({ ...prev, productCategory: option }))
                }
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <label className="tp-quote-form__field">
          <span>Company Name</span>
          <input
            type="text"
            name="companyName"
            placeholder="Enter Company Name"
            value={form.companyName}
            onChange={handleChange}
          />
        </label>

        <label className="tp-quote-form__field">
          <span>Products / SKUs</span>
          <input
            type="text"
            name="products"
            placeholder="e.g. TBS-WB-002, TBS-RPB-004"
            value={form.products}
            onChange={handleChange}
          />
        </label>

        <label className="tp-quote-form__field">
          <span>Email Address</span>
          <input
            type="email"
            name="email"
            placeholder="Enter Email Address"
            value={form.email}
            onChange={handleChange}
            required
          />
        </label>

        <label className="tp-quote-form__field">
          <span>Order Quantity</span>
          <input
            type="text"
            name="quantity"
            placeholder="Enter Order Quantity"
            value={form.quantity}
            onChange={handleChange}
          />
        </label>

        <label className="tp-quote-form__field">
          <span>Country</span>
          <input
            type="text"
            name="country"
            placeholder="Enter Country"
            value={form.country}
            onChange={handleChange}
          />
        </label>

        <label className="tp-quote-form__field">
          <span>Additional Requirements</span>
          <input
            type="text"
            name="requirements"
            placeholder="Enter Additional Requirements"
            value={form.requirements}
            onChange={handleChange}
          />
        </label>

        <label className="tp-quote-form__field">
          <span>Phone / WhatsApp Number</span>
          <input
            type="tel"
            name="phone"
            placeholder="Enter Phone / WhatsApp Number"
            value={form.phone}
            onChange={handleChange}
            required
          />
        </label>
      </div>

      {error && <p className="tp-quote-form__error">{error}</p>}

      <p className="tp-quote-form__note">
        We respond within 24 – 48 business hours.
      </p>

      <button type="submit" className="tp-btn tp-btn--primary">
        Request a Quote
        <Icon name="arrow" size={16} />
      </button>
    </form>
  );
}
